import * as React from "react";

import { cn } from "@/lib/utils";

export type SurfaceCardPadding = "none" | "sm" | "md" | "lg";

export interface SurfaceCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Internal padding step. Defaults to "md" (the standard surface padding). */
  padding?: SurfaceCardPadding;
  /** Adds the standard hover lift (subtle raise + softer shadow) for clickable cards. */
  interactive?: boolean;
}

/**
 * The base surface container — the warm white surface every other primitive
 * (stat tiles, chart frames, cover/logo tiles) sits on. One hairline border from
 * the border token, the standard corner radius, and consistent internal padding,
 * all pulled from the design tokens (src/index.css) rather than hardcoded.
 */
const PADDING: Record<SurfaceCardPadding, string> = {
  none: "",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

const SurfaceCard = React.forwardRef<HTMLDivElement, SurfaceCardProps>(
  ({ className, padding = "md", interactive = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-border bg-card text-card-foreground shadow-card",
        interactive && "hover-lift cursor-pointer",
        PADDING[padding],
        className,
      )}
      {...props}
    />
  ),
);
SurfaceCard.displayName = "SurfaceCard";

export default SurfaceCard;
